/**
 * TTS (Text-to-Speech) Service Wrapper
 * 
 * Provides a clean interface to the Capacitor TTS plugin for iOS/Android
 * with support for voice selection, queue management, and playback categories.
 */

import { TextToSpeech } from '@capacitor-community/text-to-speech';

// Queue strategy enum
export const TTSQueue = {
  Flush: 0,  // Cancel current speech and speak new text
  Add: 1     // Add to queue after current speech
};

/**
 * Speak text using native TTS
 * @param {string} text - Text to speak
 * @param {Object} opts - Optional configuration
 * @param {string} opts.lang - Language code (default: 'en-US')
 * @param {number} opts.rate - Speech rate 0.1-2.0 (default: 1.0)
 * @param {number} opts.pitch - Voice pitch 0.5-2.0 (default: 1.0)
 * @param {number} opts.volume - Volume 0.0-1.0 (default: 1.0)
 * @param {number} opts.voiceIndex - Index of voice from getSupportedVoices()
 * @param {number} opts.queue - Queue strategy (default: TTSQueue.Flush)
 * @param {string} opts.category - iOS audio category 'ambient'|'playback' (default: 'playback')
 * @returns {Promise<void>}
 */
export async function speakText(text, opts = {}) {
  // Skip empty text
  if (!text?.trim()) return;
  
  try {
    await TextToSpeech.speak({
      text,
      lang: opts.lang || 'en-US',
      rate: opts.rate || 1.0,
      pitch: opts.pitch || 1.0,
      volume: opts.volume || 1.0,
      voice: opts.voiceIndex,
      category: opts.category || 'playback', // Use playback for screen recording
      queueStrategy: opts.queue !== undefined ? opts.queue : TTSQueue.Flush
    });
  } catch (error) {
    console.error('TTS speak error:', error);
    // Fail silently - TTS is a nice-to-have feature
  }
}

/**
 * Stop all current and queued speech
 * @returns {Promise<void>}
 */
export async function stopTTS() {
  try {
    await TextToSpeech.stop();
  } catch (error) {
    console.error('TTS stop error:', error);
  }
}

/**
 * Get list of available voices on the device
 * @returns {Promise<Array>} Array of voice objects with {name, lang, voiceURI, etc.}
 */
export async function listVoices() {
  try {
    const { voices } = await TextToSpeech.getSupportedVoices();
    return voices || [];
  } catch (error) {
    console.error('TTS listVoices error:', error);
    return [];
  }
}

/**
 * Get list of supported languages
 * @returns {Promise<Array>} Array of language codes
 */
export async function getSupportedLanguages() {
  try {
    const { languages } = await TextToSpeech.getSupportedLanguages();
    return languages || [];
  } catch (error) {
    console.error('TTS getSupportedLanguages error:', error);
    return [];
  }
}

/**
 * Check if a specific language is supported
 * @param {string} lang - Language code to check
 * @returns {Promise<boolean>}
 */
export async function isLanguageSupported(lang) {
  try {
    const { supported } = await TextToSpeech.isLanguageSupported({ lang });
    return supported;
  } catch (error) {
    console.error('TTS isLanguageSupported error:', error);
    return false;
  }
}

/**
 * Open system TTS install settings (Android only)
 * @returns {Promise<void>}
 */
export async function openTTSInstall() {
  try {
    await TextToSpeech.openInstall();
  } catch (error) {
    console.error('TTS openInstall error:', error);
  }
}

/**
 * Check if TTS is available on the current platform
 * @returns {boolean}
 */
export function isTTSAvailable() {
  // Check if we're in a Capacitor/native environment
  if (typeof window !== 'undefined' && window.Capacitor) {
    return true;
  }
  
  // Fallback: check for web speech API (for development/testing)
  if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
    return true;
  }
  
  return false;
}

/**
 * Speak text with web fallback (for development)
 * This provides a fallback for web development that uses the browser's speechSynthesis API
 */
export async function speakWithFallback(text, opts = {}) {
  // Skip empty text
  if (!text?.trim()) return;
  
  // Try native TTS first
  if (window.Capacitor) {
    return speakText(text, opts);
  }
  
  // Fallback to web speech API for development
  if ('speechSynthesis' in window) {
    // Cancel previous speech
    if (opts.queue === TTSQueue.Flush || opts.queue === undefined) {
      window.speechSynthesis.cancel();
    }
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = opts.lang || 'en-US';
    utterance.rate = opts.rate || 1.0;
    utterance.pitch = opts.pitch || 1.0;
    utterance.volume = opts.volume || 1.0;
    
    window.speechSynthesis.speak(utterance);
  }
}

/**
 * Stop speech with web fallback
 */
export async function stopWithFallback() {
  // Try native TTS first
  if (window.Capacitor) {
    return stopTTS();
  }
  
  // Fallback to web speech API for development
  if ('speechSynthesis' in window) {
    window.speechSynthesis.cancel();
  }
}